import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, ChevronRight, Users } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { SECTION_COLOR, Nadpis } from '../objednavky/ui';
import { STAVY, api, cas, cil, naDatum, zasahujeDen } from './rezervaceUi';

/**
 * „Dnes přijdou" — dnešní rezervace u živých objednávek (Hosté →
 * Objednávky, období Dnes). Vedoucí směny tak vidí na jedné obrazovce, co
 * běží, i které stoly se během večera zaplní.
 *
 * Na desktopu blok ve třetím sloupci, na telefonu jeden řádek nad seznamem,
 * který otevře přehled. Klik na rezervaci vede do záložky Rezervace.
 */
export default function DnesPrijdou({ isDark, mutedColor, kompaktni = false, onOtevrit }) {
    const [rezervace, setRezervace] = useState(null);
    const [otevreno, setOtevreno] = useState(false);

    const nacti = useCallback(async () => {
        const dnes = naDatum(new Date());
        try {
            const d = await api(`/api/pos/rezervace?od=${dnes}&do=${dnes}`);
            setRezervace((d.rezervace || [])
                .filter(r => r.stav !== 'zrusena' && zasahujeDen(r, dnes))
                .sort((a, b) => a.zacatek.localeCompare(b.zacatek)));
        } catch {
            setRezervace(x => x ?? []);
        }
    }, []);

    useEffect(() => {
        nacti();
        const t = setInterval(nacti, 60000);
        const naViditelnost = () => { if (!document.hidden) nacti(); };
        document.addEventListener('visibilitychange', naViditelnost);
        return () => { clearInterval(t); document.removeEventListener('visibilitychange', naViditelnost); };
    }, [nacti]);

    if (rezervace === null) return null;
    const cekaji = rezervace.filter(r => r.stav === 'potvrzena');
    const hostu = cekaji.reduce((n, r) => n + r.pocet_hostu, 0);
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';

    const seznam = (
        <div className="flex flex-col">
            {rezervace.map(r => {
                const s = STAVY[r.stav] || STAVY.potvrzena;
                return (
                    <button key={r.id} type="button" onClick={() => { setOtevreno(false); onOtevrit?.(r); }}
                        className="w-full flex items-center text-left"
                        style={{
                            gap: 10, padding: kompaktni ? '10px 16px' : '7px 0', minHeight: kompaktni ? 56 : 44,
                            border: 'none', borderTop: `1px solid ${cara}`, background: 'transparent', cursor: 'pointer',
                            opacity: r.stav === 'posazena' || r.stav === 'nedorazila' ? 0.6 : 1,
                        }}>
                        <span style={{ width: 40, flexShrink: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                            {r.cely_den ? 'den' : cas(r.zacatek)}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                                {r.jmeno} · {r.pocet_hostu}
                            </span>
                            <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: mutedColor }}>
                                {[cil(r), r.poznamka].filter(Boolean).join(' · ')}
                            </span>
                        </span>
                        <span className="rounded-full" style={{ padding: '1px 7px', fontSize: 10.5, fontWeight: 600, background: `${s.color}26`, color: s.color, flexShrink: 0 }}>{s.label}</span>
                    </button>
                );
            })}
        </div>
    );

    if (kompaktni) {
        if (!rezervace.length) return null;
        return (
            <>
                <button type="button" onClick={() => setOtevreno(true)}
                    className="flex items-center w-full text-left"
                    style={{
                        gap: 10, height: 48, padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                        color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, WebkitTapHighlightColor: 'transparent',
                    }}>
                    <CalendarClock size={17} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                    <span className="flex-1 min-w-0 truncate">Dnes přijdou</span>
                    <span className="tabular-nums" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {cekaji.length ? `${cekaji.length} · ${hostu} os.` : 'všichni tu'}
                    </span>
                    <ChevronRight size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                </button>
                {otevreno && (
                    <ModalShell onClose={() => setOtevreno(false)} isDark={isDark} barva={SECTION_COLOR} ikona={CalendarClock}
                        nadpis={`Dnes přijdou · ${rezervace.length}`}>
                        {seznam}
                    </ModalShell>
                )}
            </>
        );
    }

    return (
        <>
            <Nadpis muted={mutedColor} style={{ marginTop: 16 }}>
                <span className="flex items-center justify-between" style={{ gap: 8 }}>
                    <span>Dnes přijdou</span>
                    {cekaji.length > 0 && (
                        <span className="inline-flex items-center" style={{ gap: 3, textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
                            {cekaji.length} · <Users size={11} />{hostu}
                        </span>
                    )}
                </span>
            </Nadpis>
            {rezervace.length ? seznam : <p style={{ margin: '6px 0 0', fontSize: 12.5, color: mutedColor }}>Na dnešek žádná rezervace.</p>}
        </>
    );
}
